import { axiosInstance } from './axios';
import type {
    IStockLevel,
    IUpdateStockThresholds,
    IStockAdjustment,
    IStockReservation,
    IStockAvailability,
    ILowStockItem,
    IStockMovement,
    IInitializeStock,
    IStockStats
} from '../interfaces/stock';

const BASE_URL = '/Stock';

export const stockService = {
    // Get all stock levels
    getAllStockLevels: async () => {
        const response = await axiosInstance.get<IStockLevel[]>(BASE_URL);
        return response.data;
    },

    // Get stock level for a product
    getStockLevel: async (inventoryItemId: number) => {
        const response = await axiosInstance.get<IStockLevel>(`${BASE_URL}/${inventoryItemId}`);
        return response.data;
    },

    // Update stock thresholds
    updateStockThresholds: async (inventoryItemId: number, data: IUpdateStockThresholds) => {
        const response = await axiosInstance.put<IStockLevel>(`${BASE_URL}/${inventoryItemId}/thresholds`, data);
        return response.data;
    },

    // Initialize stock
    initializeStock: async (data: IInitializeStock) => {
        const response = await axiosInstance.post<IStockLevel>(`${BASE_URL}/initialize`, data);
        return response.data;
    },

    // Adjust stock
    adjustStock: async (data: IStockAdjustment) => {
        const response = await axiosInstance.post<IStockLevel>(`${BASE_URL}/adjust`, data);
        return response.data;
    },

    // Increase stock
    increaseStock: async (inventoryItemId: number, quantity: number, reason: string = 'Manual adjustment') => {
        const response = await axiosInstance.post<IStockLevel>(
            `${BASE_URL}/${inventoryItemId}/increase`,
            quantity,
            { params: { reason } }
        );
        return response.data;
    },

    // Decrease stock
    decreaseStock: async (inventoryItemId: number, quantity: number, reason: string = 'Manual adjustment') => {
        const response = await axiosInstance.post<IStockLevel>(
            `${BASE_URL}/${inventoryItemId}/decrease`,
            quantity,
            { params: { reason } }
        );
        return response.data;
    },

    // Reserve stock
    reserveStock: async (data: IStockReservation) => {
        const response = await axiosInstance.post<IStockLevel>(`${BASE_URL}/reserve`, data);
        return response.data;
    },

    // Release stock
    releaseStock: async (data: IStockReservation) => {
        const response = await axiosInstance.post<IStockLevel>(`${BASE_URL}/release`, data);
        return response.data;
    },

    // Check availability
    checkAvailability: async (inventoryItemId: number, requiredQuantity: number) => {
        const response = await axiosInstance.get<IStockAvailability>(
            `${BASE_URL}/${inventoryItemId}/availability`,
            { params: { requiredQuantity } }
        );
        return response.data;
    },

    // Get low stock items
    getLowStockItems: async () => {
        const response = await axiosInstance.get<ILowStockItem[]>(`${BASE_URL}/alerts/low-stock`);
        return response.data;
    },

    // Get out of stock items
    getOutOfStockItems: async () => {
        const response = await axiosInstance.get<ILowStockItem[]>(`${BASE_URL}/alerts/out-of-stock`);
        return response.data;
    },

    // Get stock movements for a product
    getStockMovements: async (inventoryItemId: number) => {
        const response = await axiosInstance.get<IStockMovement[]>(`${BASE_URL}/${inventoryItemId}/movements`);
        return response.data;
    },

    // Get recent stock movements
    getRecentStockMovements: async (count: number = 50) => {
        const response = await axiosInstance.get<IStockMovement[]>(`${BASE_URL}/movements/recent`, {
            params: { count }
        });
        return response.data;
    },

    // Get stock statistics
    getStockStatistics: async () => {
        const response = await axiosInstance.get<IStockStats>(`${BASE_URL}/statistics`);
        return response.data;
    },
};
